"use client"

import { Calendar, Users, FileText, ListChecks, FileCheck, Edit, Trash2, Send } from "lucide-react"

export interface Assessment {
  id: string
  title: string
  category: string
  status: "active" | "closed" | "draft"
  dueDate: string
  sharedWith: string
  type: string
  questions: string
  resources: Array<{ type: string; icon: string }>
  isAssigned?: boolean
}

interface AssessmentCardProps {
  assessment: Assessment
  onAssign?: (id: string) => void
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
}

export function AssessmentCard({ assessment, onAssign, onEdit, onDelete }: AssessmentCardProps) {
  const statusStyles = {
    active: "bg-[#13ecda]/20 text-gray-900 border-[#13ecda]/30",
    closed: "bg-gray-100 text-gray-500 border-gray-200",
    draft: "bg-yellow-100 text-yellow-700 border-yellow-200",
  }

  const statusDot = {
    active: "bg-green-500",
    closed: "bg-gray-400",
    draft: "bg-yellow-500",
  }

  const getResourceIcon = (type: string) => {
    switch (type) {
      case "pdf":
        return <FileCheck className="h-4 w-4 text-red-500" />
      case "video":
        return <FileText className="h-4 w-4 text-blue-500" />
      case "doc":
        return <FileText className="h-4 w-4 text-blue-500" />
      default:
        return <FileText className="h-4 w-4 text-gray-500" />
    }
  }

  return (
    <div className="group flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">{assessment.title}</h3>
          <p className="text-xs text-gray-500 font-medium">{assessment.category}</p>
        </div>
        <span
          className={`${statusStyles[assessment.status]} px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1`}
        >
          <span className={`h-2 w-2 rounded-full ${statusDot[assessment.status]} block`}></span>
          {assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}
        </span>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-y-4 gap-x-2 mb-6">
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1 font-medium">Due Date</span>
          <span className="text-sm font-bold text-gray-900 flex items-center gap-1">
            <Calendar className="h-4 w-4" /> {assessment.dueDate}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1 font-medium">Shared With</span>
          <span className="text-sm font-bold text-gray-900 flex items-center gap-1">
            <Users className="h-4 w-4" /> {assessment.sharedWith}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1 font-medium">Type</span>
          <span className="text-sm font-bold text-gray-900 flex items-center gap-1">
            <FileText className="h-4 w-4" /> {assessment.type}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1 font-medium">Questions</span>
          <span className="text-sm font-bold text-gray-900 flex items-center gap-1">
            <ListChecks className="h-4 w-4" /> {assessment.questions}
          </span>
        </div>
      </div>

      {/* Resources */}
      <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg border border-gray-100">
        <span className="text-xs font-bold text-gray-500 uppercase">Resources:</span>
        <div className="flex -space-x-2">
          {assessment.resources.map((resource, index) => (
            <div
              key={index}
              className="h-8 w-8 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm"
              title={resource.type}
            >
              {getResourceIcon(resource.type)}
            </div>
          ))}
        </div>
        {assessment.resources.length > 2 && (
          <span className="text-xs text-gray-500 font-medium ml-1">+ {assessment.resources.length - 2} files</span>
        )}
      </div>

      {/* Actions */}
      <div className="mt-auto grid grid-cols-2 gap-3">
        <button
          onClick={() => onAssign?.(assessment.id)}
          disabled={assessment.isAssigned}
          className={`col-span-2 ${
            assessment.isAssigned
              ? "bg-gray-50 text-gray-400 cursor-not-allowed"
              : "bg-gray-900 text-white hover:bg-black/90"
          } font-bold py-2.5 px-4 rounded-lg text-sm transition-colors flex items-center justify-center gap-2`}
        >
          {assessment.isAssigned ? (
            <>
              <FileCheck className="h-5 w-5" /> Assigned
            </>
          ) : (
            <>
              <Send className="h-5 w-5" /> Assign to Class
            </>
          )}
        </button>
        <button
          onClick={() => onEdit?.(assessment.id)}
          className="bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 font-bold py-2 px-4 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
        >
          <Edit className="h-5 w-5" /> Edit
        </button>
        <button
          onClick={() => onDelete?.(assessment.id)}
          className="bg-red-50 text-red-600 hover:bg-red-100 font-bold py-2 px-4 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 className="h-5 w-5" /> Delete
        </button>
      </div>
    </div>
  )
}
